/** View Toggle — pure helpers for VT1 (which options render) and VT2 (which view loads first). */

import { isNonBlank } from "./is-non-blank.shared";
import type { ChannelLineupView } from "./channel-lineup";

/**
 * The lineup's closed vocabulary, exported so every reader of an authored
 * view value (`resolveLineupForBlok` in `app/components/channel-lineup/lineup-seam.ts`, the
 * adapter's `DDS_CHANNEL_LINEUP` case) checks against the same set rather than re-typing
 * `"signature" | "genre"` as a literal that can drift from `channel-lineup.tsx`'s own type.
 */
export const CHANNEL_LINEUP_VIEWS: Record<ChannelLineupView, true> = {
  signature: true,
  genre: true
};

export interface ViewToggleOption {
  /** Stable identity, and the value the no-JS form submits under `name`. */
  value: string;
  /** Content-managed wording. VT2 step 1 matches whichever option's label
   *  contains the word "genre" (case-insensitive) against a `?view=genre`
   *  link — "whatever the option is called," per the audit. */
  label: string;
}

/**
 * VT1 — drops anything malformed or duplicated, the same contract `tab-tray.tsx`'s
 * `selectRenderableItems` enforces for its own radio group.
 */
export function prepareViewToggleOptions(options?: ViewToggleOption[]): {
  renderable: ViewToggleOption[];
  malformed: number;
  duplicates: string[];
} {
  const renderable: ViewToggleOption[] = [];
  const duplicates: string[] = [];
  const seen = new Set<string>();
  let malformed = 0;

  for (const option of options ?? []) {
    if (!isNonBlank(option?.value) || !isNonBlank(option?.label)) {
      malformed += 1;
      continue;
    }

    if (seen.has(option.value)) {
      duplicates.push(option.value);
      continue;
    }

    seen.add(option.value);
    renderable.push(option);
  }

  return {
    renderable,
    malformed,
    duplicates
  };
}

/**
 * Whether an authored label names the genre view — VT2 step 1. Exported so a consumer resolving
 * `requestedGenreView` from the same option list (the organism root, deciding what `?view=genre`
 * means for its blok) reads one implementation rather than a second regex.
 */
export function isGenreLabel(label: string): boolean {
  return label.toLowerCase().includes("genre");
}

export interface ResolveInitialViewInput {
  /** Already VALIDATED (`prepareViewToggleOptions`'s `renderable`), in the
   *  content team's authored order. */
  options: ViewToggleOption[];
  /** The incoming request asked for the genre view (`?view=genre`). */
  requestedGenreView: boolean;
  /** The visitor's referrer is one of the content-managed "genre trigger"
   *  pages (Genre Packs, MyEntertainment, MyNews, MySports, MiEspañol,
   *  build-your-package) — VT2 step 2. */
  arrivedFromTriggerPage: boolean;
  /** At least one trigger page is configured, independent of whether THIS
   *  visitor came from one — VT2 steps 3/4 key off this, not off
   *  `arrivedFromTriggerPage` alone. */
  triggerPagesConfigured: boolean;
  /** The option value the content team marked as default — VT2 step 4. */
  defaultValue?: string;
}

/** VT2's four-step resolution, in the exact order `acs/VT.md` gives it. */
export function resolveInitialView({
  options,
  requestedGenreView,
  arrivedFromTriggerPage,
  triggerPagesConfigured,
  defaultValue
}: ResolveInitialViewInput): string | undefined {
  if (options.length === 0) {
    return undefined;
  }

  /* Step 1 — the link itself asked for genre, regardless of trigger pages. */
  if (requestedGenreView) {
    const genre = options.find(option => isGenreLabel(option.label));
    if (genre) {
      return genre.value;
    }
    /* No option is labelled "genre" at all — falls through rather than
       resolving to nothing; a misconfigured label is not a reason to leave
       the view unresolved. */
  }

  /* Step 2 — a genre TRIGGER page, by POSITION ("the second option"), not by
     searching for whichever option says "genre". */
  if (arrivedFromTriggerPage && options[1]) {
    return options[1].value;
  }

  /* Step 3 — trigger pages exist but this visitor did not come from one. */
  if (triggerPagesConfigured) {
    return options[0].value;
  }

  /* Step 4 — no trigger pages configured at all: the content team's default,
     or the first option if none is marked. */
  const marked = options.find(option => option.value === defaultValue);
  return (marked ?? options[0]).value;
}
